<template>
  <div>
    <div class="page-header-row">
      <h4 class="table-page-header">Payment Tracking</h4>
      <div class="header-actions">
        <el-button
          type="success"
          @click="openCreatePaymentModal"
          icon="el-icon-plus"
        >
          Create Payment
        </el-button>
        <el-button
          type="primary"
          @click="openUploadModal"
          icon="el-icon-upload2"
        >
          Upload CSV
        </el-button>
        <el-button
          v-if="reconciliationData.length > 0"
          type="success"
          @click="downloadCSV"
          :loading="loadingDownload"
        >
          Download CSV
        </el-button>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-container">
      <div class="filter-group-compact">
        <AIVSelect
          v-model="dateRangeType"
          :options="dateRangeTypeOptions"
          placeholder="Period"
          :searchable="false"
          :clearable="false"
          class="compact-select"
          @input="handleDateRangeTypeChange"
        />
      </div>

      <!-- Custom Date Range Picker -->
      <div v-if="dateRangeType === 'custom'" class="filter-group-compact date-range-group">
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="-"
          start-placeholder="Start date"
          end-placeholder="End date"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          class="compact-date-picker"
        />
      </div>

      <!-- Period-based Date Picker with Navigation -->
      <template v-if="dateRangeType !== 'custom'">
        <div class="filter-group-compact date-group date-nav-group">
          <button @click="goToPrevious" class="compact-nav-btn">
            <i class="fas fa-chevron-left"></i>
          </button>
          <el-date-picker
            ref="datePicker"
            v-model="selectedDate"
            type="month"
            format="MMMM yyyy"
            placeholder="Select month"
            class="compact-date-picker"
            value-format="yyyy-MM-dd"
          />
          <button @click="goToNext" class="compact-nav-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </template>

      <div class="filter-group-compact">
        <AIVSelect
          v-model="filters.clientId"
          :options="clientOptions"
          placeholder="Client"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div class="filter-group-compact">
        <AIVSelect
          v-model="filters.serviceCodeIds"
          :options="serviceCodeOptions"
          placeholder="Service Code"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div class="filter-group-compact">
        <AIVSelect
          v-model="filters.statuses"
          :options="statusOptions"
          placeholder="Status"
          :clearable="true"
          :searchable="false"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
    </div>

    <!-- Summary Cards -->
    <div v-if="reconciliationData.length > 0" class="summary-cards">
      <div class="summary-card">
        <div class="summary-label">Total Billed</div>
        <div class="summary-value">${{ formatCurrency(summary.totalBilled) }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Total Paid</div>
        <div class="summary-value text-success">${{ formatCurrency(summary.totalPaid) }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Total Outstanding</div>
        <div class="summary-value text-danger">${{ formatCurrency(summary.totalOutstanding) }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Match Rate</div>
        <div class="summary-value">{{ summary.matchRate }}%</div>
      </div>
    </div>

    <!-- Reconciliation Table -->
    <div v-if="reconciliationData.length > 0" class="mt-4">
      <AIVTable
        :columns="tableColumns"
        :data="reconciliationData"
        :pagination="pagination"
        item-name="records"
        @pageChange="handlePageChange"
        @pageSizeChange="handlePageSizeChange"
        @sort-change="handleSortChange"
      >
        <template #cell-status="{ row }">
          <el-tag
            :type="getStatusTagType(row.status)"
            size="small"
          >
            {{ row.status }}
          </el-tag>
        </template>
        <template #cell-actions="{ row }">
          <el-button
            size="mini"
            type="warning"
            icon="el-icon-edit"
            @click="openEditPaymentModal(row)"
          >
            Edit
          </el-button>
          <el-button
            size="mini"
            type="danger"
            icon="el-icon-delete"
            @click="deletePayment(row)"
          >
            Delete
          </el-button>
          <el-button
            size="mini"
            type="primary"
            @click="openAdjustModal(row)"
          >
            Adjust
          </el-button>
        </template>
      </AIVTable>
    </div>

    <!-- Upload Modal -->
    <AIVDialog
      v-model="showUploadModal"
      title="Upload Payment CSV"
      width="900px"
    >
      <div class="upload-modal-body">
        <div class="upload-area">
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            @change="handleFileSelect"
            class="file-input"
          />
          <div v-if="!selectedFile" class="upload-placeholder" @click="$refs.fileInput.click()">
            <i class="fa fa-cloud-upload-alt upload-icon"></i>
            <p>Click to select a CSV file</p>
          </div>
          <div v-else class="file-selected">
            <i class="fa fa-file-csv"></i>
            <span>{{ selectedFile.name }}</span>
            <button class="remove-file-btn" @click="clearFile">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <div v-if="uploadPreview.length > 0 && !uploadResults" class="upload-preview mt-3">
          <h6>Preview (first {{ uploadPreview.length }} rows)</h6>
          <div class="preview-table-wrapper">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="header in previewHeaders" :key="header">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in uploadPreview" :key="idx">
                  <td v-for="header in previewHeaders" :key="header">{{ row[header] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Results Section -->
        <div v-if="uploadResults" class="bulk-import-results mt-4">
          <h6 style="margin-bottom: 12px">Import Results</h6>
          <div class="row text-center mb-3">
            <div class="col-4">
              <div class="bulk-stat bulk-stat-success">
                <div class="bulk-stat-number">{{ uploadResults.summary.created }}</div>
                <div class="bulk-stat-label">Created</div>
              </div>
            </div>
            <div class="col-4">
              <div class="bulk-stat bulk-stat-warning">
                <div class="bulk-stat-number">{{ uploadResults.summary.skipped }}</div>
                <div class="bulk-stat-label">Skipped</div>
              </div>
            </div>
            <div class="col-4">
              <div class="bulk-stat bulk-stat-danger">
                <div class="bulk-stat-number">{{ uploadResults.summary.errors }}</div>
                <div class="bulk-stat-label">Errors</div>
              </div>
            </div>
          </div>

          <!-- Created detail -->
          <div v-if="uploadResults.details.created.length > 0" class="mt-3">
            <h6 style="color: #67c23a">Created ({{ uploadResults.details.created.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Service</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in uploadResults.details.created" :key="'c-' + item.row">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.svcCode }}</td>
                    <td>${{ parseFloat(item.amount).toFixed(2) }}</td>
                    <td>{{ item.status }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Skipped detail -->
          <div v-if="uploadResults.details.skipped.length > 0" class="mt-3">
            <h6 style="color: #e6a23c">Skipped ({{ uploadResults.details.skipped.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Service</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in uploadResults.details.skipped" :key="'s-' + idx">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.svcCode }}</td>
                    <td>{{ item.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Errors detail -->
          <div v-if="uploadResults.details.errors.length > 0" class="mt-3">
            <h6 style="color: #f56c6c">Errors ({{ uploadResults.details.errors.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in uploadResults.details.errors" :key="'e-' + idx">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="dialog-footer">
          <button class="btn btn-secondary" @click="showUploadModal = false">
            {{ uploadResults ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-if="!uploadResults"
            class="btn btn-primary"
            :disabled="!selectedFile || uploading"
            @click="handleUpload"
          >
            <span v-if="uploading">
              <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
              Uploading...
            </span>
            <span v-else>Upload</span>
          </button>
        </div>
      </template>
    </AIVDialog>

    <!-- Adjustment Modal -->
    <AIVDialog
      v-model="showAdjustModal"
      title="Adjust Payment"
      width="500px"
      height="auto"
    >
      <div v-if="adjustingRow" class="adjust-modal-body">
        <div class="adjust-field">
          <label>Client</label>
          <div class="adjust-value">{{ adjustingRow.client_name }}</div>
        </div>
        <div class="adjust-field">
          <label>Service Code</label>
          <div class="adjust-value">{{ adjustingRow.service_code }}</div>
        </div>
        <div class="adjust-field">
          <label>Billed Amount</label>
          <div class="adjust-value">${{ formatCurrency(adjustingRow.billed_amount) }}</div>
        </div>
        <div class="adjust-field">
          <label>Current Paid Amount</label>
          <div class="adjust-value">${{ formatCurrency(adjustingRow.paid_amount) }}</div>
        </div>
        <div class="adjust-field">
          <label>New Paid Amount</label>
          <input
            v-model.number="adjustForm.paidAmount"
            type="number"
            step="0.01"
            min="0"
            class="adjust-input"
          />
        </div>
        <div class="adjust-field">
          <label>Notes</label>
          <textarea
            v-model="adjustForm.notes"
            class="adjust-textarea"
            rows="3"
            placeholder="Reason for adjustment..."
          ></textarea>
        </div>
      </div>

      <template slot="footer">
        <div class="dialog-footer">
          <button class="btn btn-secondary" @click="showAdjustModal = false">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="adjusting"
            @click="handleAdjust"
          >
            <span v-if="adjusting">
              <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
              Saving...
            </span>
            <span v-else>Save</span>
          </button>
        </div>
      </template>
    </AIVDialog>

    <!-- Create/Edit Payment Modal -->
    <AIVDialog
      v-model="showPaymentModal"
      :title="paymentModalMode === 'create' ? 'Create Payment' : 'Edit Payment'"
      width="650px"
      height="auto"
    >
      <div class="payment-modal-body">
        <div class="payment-form-row">
          <div class="payment-form-field">
            <label>Client</label>
            <AIVSelect
              v-model="paymentForm.id_clients"
              :options="clientOptions"
              placeholder="Select client"
              :searchable="true"
              :clearable="true"
            />
          </div>
          <div class="payment-form-field">
            <label>Service Code</label>
            <AIVSelect
              v-model="paymentForm.id_service_codes"
              :options="serviceCodeOptions"
              placeholder="Select service code"
              :searchable="true"
              :clearable="true"
            />
          </div>
        </div>

        <div class="payment-form-row">
          <div class="payment-form-field">
            <label>Period Start</label>
            <el-date-picker
              v-model="paymentForm.period_start"
              type="date"
              placeholder="Start date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              class="full-width-picker"
            />
          </div>
          <div class="payment-form-field">
            <label>Period End</label>
            <el-date-picker
              v-model="paymentForm.period_end"
              type="date"
              placeholder="End date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              class="full-width-picker"
            />
          </div>
        </div>

        <div class="payment-form-row">
          <div class="payment-form-field">
            <label>Rate</label>
            <input
              v-model.number="paymentForm.rate"
              type="number"
              step="0.01"
              min="0"
              class="adjust-input"
              placeholder="0.00"
            />
          </div>
          <div class="payment-form-field">
            <label>Units Billed</label>
            <input
              v-model.number="paymentForm.units_billed"
              type="number"
              step="0.01"
              min="0"
              class="adjust-input"
              placeholder="0.00"
            />
          </div>
          <div class="payment-form-field">
            <label>Billed Amount</label>
            <input
              v-model.number="paymentForm.billed_amount"
              type="number"
              step="0.01"
              min="0"
              class="adjust-input"
              placeholder="0.00"
            />
          </div>
        </div>

        <div class="payment-form-row">
          <div class="payment-form-field">
            <label>Paid Amount</label>
            <input
              v-model.number="paymentForm.paid_amount"
              type="number"
              step="0.01"
              min="0"
              class="adjust-input"
              placeholder="0.00"
            />
          </div>
          <div class="payment-form-field">
            <label>Paid Date</label>
            <el-date-picker
              v-model="paymentForm.paid_date"
              type="date"
              placeholder="Paid date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              class="full-width-picker"
            />
          </div>
        </div>

        <div class="payment-form-row">
          <div class="payment-form-field">
            <label>Contract #</label>
            <input
              v-model="paymentForm.contract_number"
              type="text"
              class="adjust-input"
              placeholder="Contract number"
            />
          </div>
          <div class="payment-form-field">
            <label>Warrant #</label>
            <input
              v-model="paymentForm.warrant_number"
              type="text"
              class="adjust-input"
              placeholder="Warrant number"
            />
          </div>
        </div>

        <div class="payment-form-row">
          <div class="payment-form-field">
            <label>Office</label>
            <input
              v-model="paymentForm.office"
              type="text"
              class="adjust-input"
              placeholder="Office"
            />
          </div>
          <div class="payment-form-field">
            <label>Kind</label>
            <input
              v-model="paymentForm.kind"
              type="text"
              class="adjust-input"
              maxlength="1"
              placeholder="Q, D, or S"
            />
          </div>
        </div>

        <div class="payment-form-field mt-3">
          <label>Notes</label>
          <textarea
            v-model="paymentForm.notes"
            class="adjust-textarea"
            rows="3"
            placeholder="Optional notes..."
          ></textarea>
        </div>
      </div>

      <template slot="footer">
        <div class="dialog-footer">
          <button class="btn btn-secondary" @click="showPaymentModal = false">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="savingPayment || !paymentForm.id_clients || !paymentForm.period_start || !paymentForm.period_end"
            @click="savePayment"
          >
            <span v-if="savingPayment">
              <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
              Saving...
            </span>
            <span v-else>{{ paymentModalMode === 'create' ? 'Create' : 'Save' }}</span>
          </button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { paymentTrackingService } from '@/serviceHandlers/paymentTrackingService';
import { clientsService } from '@/serviceHandlers/clientsService';
import { reportsService } from '@/serviceHandlers/reportsService';

export default {
  name: 'PaymentTracking',
  data() {
    return {
      dateRangeType: 'month',
      dateRangeTypeOptions: [
        { id: 'month', name: 'Month' },
        { id: 'custom', name: 'Custom' },
      ],
      selectedDate: null,
      filters: {
        dateRange: null,
        clientId: [],
        serviceCodeIds: [],
        statuses: [],
      },
      statusOptions: [
        { id: 'Paid', name: 'Paid' },
        { id: 'Partial', name: 'Partial' },
        { id: 'Unpaid', name: 'Unpaid' },
      ],
      clientOptions: [],
      serviceCodeOptions: [],
      reconciliationData: [],
      summary: {
        totalBilled: 0,
        totalPaid: 0,
        totalOutstanding: 0,
        matchRate: 0,
      },
      sortProp: '',
      sortOrder: '',
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 1,
      },
      tableColumns: [
        { label: 'Client Name', key: 'client_name', sortable: true },
        { label: 'Service Code', key: 'service_code', sortable: true },
        { label: 'Billed Amount', key: 'billed_amount', sortable: true },
        { label: 'Paid Amount', key: 'paid_amount', sortable: true },
        { label: 'Difference', key: 'difference', sortable: true },
        { label: 'Status', key: 'status', sortable: true },
        { label: 'Actions', key: 'actions', sortable: false, width: '260px' },
      ],
      loadingDownload: false,
      // Upload modal
      showUploadModal: false,
      selectedFile: null,
      uploadPreview: [],
      previewHeaders: [],
      uploading: false,
      uploadResults: null,
      // Adjust modal
      showAdjustModal: false,
      adjustingRow: null,
      adjustForm: {
        paidAmount: 0,
        notes: '',
      },
      adjusting: false,
      // Create/Edit payment modal
      showPaymentModal: false,
      paymentModalMode: 'create',
      editingPaymentId: null,
      savingPayment: false,
      paymentForm: {
        id_clients: null,
        id_service_codes: null,
        period_start: null,
        period_end: null,
        rate: null,
        units_billed: null,
        billed_amount: null,
        paid_amount: null,
        paid_date: null,
        contract_number: '',
        warrant_number: '',
        office: '',
        kind: '',
        notes: '',
      },
    };
  },
  mounted() {
    this.loadFilterOptions();
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    this.selectedDate = `${year}-${month}-${day}`;
  },
  watch: {
    selectedDate: {
      handler(newValue) {
        if (newValue && this.dateRangeType !== 'custom') {
          this.updateDateRangeFromSelectedDate();
        }
      },
    },
    'filters.dateRange': {
      handler(newValue) {
        if (newValue && newValue.length === 2) {
          this.pagination.currentPage = 1;
          this.fetchReconciliation();
        }
      },
    },
    'filters.clientId': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.fetchReconciliation();
        }
      },
    },
    'filters.serviceCodeIds': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.fetchReconciliation();
        }
      },
    },
    'filters.statuses': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.fetchReconciliation();
        }
      },
    },
  },
  methods: {
    async loadFilterOptions() {
      try {
        const clients = await clientsService.list({ slim: true });
        this.clientOptions = (clients || []).map(client => ({
          id: client.id,
          name: `${client.first_name} ${client.last_name}`,
        }));

        const serviceCodes = await reportsService.getAllCodes();
        this.serviceCodeOptions = (serviceCodes || [])
          .filter(code => code.type === 'service')
          .map(code => ({
            id: parseInt(code.id.replace('service_', '')),
            name: code.code,
          }));
      } catch (error) {
        this.$notifyError('Failed to load filter options');
      }
    },

    async fetchReconciliation() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          statuses: this.filters.statuses.length > 0 ? this.filters.statuses.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };

        const response = await paymentTrackingService.getReconciliation(params);

        if (response.success && response.data) {
          this.reconciliationData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (response.summary) {
            this.summary = response.summary;
          } else {
            this.computeSummary();
          }
          if (this.reconciliationData.length === 0) {
            this.$notifyError('No reconciliation data found');
          }
        } else {
          this.reconciliationData = [];
          this.summary = { totalBilled: 0, totalPaid: 0, totalOutstanding: 0, matchRate: 0 };
        }
      } catch (error) {
        this.$notifyError('Failed to load reconciliation data');
      }
    },

    computeSummary() {
      const totalBilled = this.reconciliationData.reduce((sum, r) => sum + (parseFloat(r.billed_amount) || 0), 0);
      const totalPaid = this.reconciliationData.reduce((sum, r) => sum + (parseFloat(r.paid_amount) || 0), 0);
      const totalOutstanding = totalBilled - totalPaid;
      const paidCount = this.reconciliationData.filter(r => r.status === 'Paid').length;
      const matchRate = this.reconciliationData.length > 0
        ? Math.round((paidCount / this.reconciliationData.length) * 100)
        : 0;
      this.summary = { totalBilled, totalPaid, totalOutstanding, matchRate };
    },

    async downloadCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }

      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          statuses: this.filters.statuses.length > 0 ? this.filters.statuses.join(',') : null,
        };

        const blob = await paymentTrackingService.downloadReconciliation(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `payment_reconciliation_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    openUploadModal() {
      this.selectedFile = null;
      this.uploadPreview = [];
      this.previewHeaders = [];
      this.uploading = false;
      this.uploadResults = null;
      this.showUploadModal = true;
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.parsePreview(file);
    },

    parseCsvLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      result.push(current.trim());
      return result;
    },

    parsePreview(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) {
          this.uploadPreview = [];
          this.previewHeaders = [];
          return;
        }

        const headers = this.parseCsvLine(lines[0]);
        this.previewHeaders = headers;

        const previewRows = lines.slice(1, 6).map(line => {
          const values = this.parseCsvLine(line);
          const row = {};
          headers.forEach((h, i) => { row[h] = values[i] || ''; });
          return row;
        });
        this.uploadPreview = previewRows;
      };
      reader.readAsText(file);
    },

    clearFile() {
      this.selectedFile = null;
      this.uploadPreview = [];
      this.previewHeaders = [];
      this.uploadResults = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async handleUpload() {
      if (!this.selectedFile) return;

      this.uploading = true;
      try {
        const response = await paymentTrackingService.uploadPaymentCsv(this.selectedFile);
        if (response.success) {
          this.uploadResults = response;

          if (response.summary && response.summary.created > 0) {
            this.$notifySuccess(`Successfully imported ${response.summary.created} payment(s)`);
            this.fetchReconciliation();
          } else if (response.summary && response.summary.created === 0 && response.summary.skipped > 0) {
            this.$notifyInfo('All rows were skipped (duplicates or empty)');
          }
        } else {
          this.$notifyError(response.message || 'Failed to process CSV');
        }
      } catch (error) {
        this.$notifyError(error.response?.data?.message || 'Failed to upload CSV');
      } finally {
        this.uploading = false;
      }
    },

    openAdjustModal(row) {
      this.adjustingRow = row;
      this.adjustForm.paidAmount = parseFloat(row.paid_amount) || 0;
      this.adjustForm.notes = '';
      this.showAdjustModal = true;
    },

    async handleAdjust() {
      if (!this.adjustingRow) return;

      this.adjusting = true;
      try {
        const response = await paymentTrackingService.adjustPayment(this.adjustingRow.id, {
          paid_amount: this.adjustForm.paidAmount,
          notes: this.adjustForm.notes,
        });
        if (response.success) {
          this.$notifySuccess('Payment adjusted successfully');
          this.showAdjustModal = false;
          this.fetchReconciliation();
        } else {
          this.$notifyError(response.message || 'Failed to adjust payment');
        }
      } catch (error) {
        this.$notifyError('Failed to adjust payment');
      } finally {
        this.adjusting = false;
      }
    },

    openCreatePaymentModal() {
      this.paymentModalMode = 'create';
      this.editingPaymentId = null;
      this.paymentForm = {
        id_clients: null,
        id_service_codes: null,
        period_start: null,
        period_end: null,
        rate: null,
        units_billed: null,
        billed_amount: null,
        paid_amount: null,
        paid_date: null,
        contract_number: '',
        warrant_number: '',
        office: '',
        kind: '',
        notes: '',
      };
      this.showPaymentModal = true;
    },

    openEditPaymentModal(row) {
      this.paymentModalMode = 'edit';
      this.editingPaymentId = row.id;
      this.paymentForm = {
        id_clients: row.id_clients,
        id_service_codes: row.id_service_codes,
        period_start: row.period_start,
        period_end: row.period_end,
        rate: row.rate != null ? parseFloat(row.rate) : null,
        units_billed: row.units_billed != null ? parseFloat(row.units_billed) : null,
        billed_amount: parseFloat(row.billed_amount) || null,
        paid_amount: parseFloat(row.paid_amount) || null,
        paid_date: row.paid_date,
        contract_number: row.contract_number || '',
        warrant_number: row.warrant_number || '',
        office: row.office || '',
        kind: row.kind || '',
        notes: row.notes || '',
      };
      this.showPaymentModal = true;
    },

    async savePayment() {
      this.savingPayment = true;
      try {
        const payload = { ...this.paymentForm };
        // Auto-compute billed_amount from rate × units if not manually set
        if (!payload.billed_amount && payload.rate && payload.units_billed) {
          payload.billed_amount = (payload.rate * payload.units_billed).toFixed(2);
        }

        let response;
        if (this.paymentModalMode === 'create') {
          response = await paymentTrackingService.createPayment(payload);
        } else {
          response = await paymentTrackingService.updatePayment(this.editingPaymentId, payload);
        }

        if (response.success) {
          this.$notifySuccess(
            this.paymentModalMode === 'create' ? 'Payment created successfully' : 'Payment updated successfully'
          );
          this.showPaymentModal = false;
          this.fetchReconciliation();
        } else {
          this.$notifyError(response.message || 'Failed to save payment');
        }
      } catch (error) {
        this.$notifyError(error.response?.data?.message || 'Failed to save payment');
      } finally {
        this.savingPayment = false;
      }
    },

    async deletePayment(row) {
      const confirmed = await this.$confirmAction(
        `Are you sure you want to delete this payment record for ${row.client_name}?`,
        'Delete Payment'
      );
      if (!confirmed) return;

      try {
        const response = await paymentTrackingService.deletePayment(row.id);
        if (response.success) {
          this.$notifySuccess('Payment deleted successfully');
          this.fetchReconciliation();
        } else {
          this.$notifyError(response.message || 'Failed to delete payment');
        }
      } catch (error) {
        this.$notifyError('Failed to delete payment');
      }
    },

    getStatusTagType(status) {
      switch (status) {
        case 'Paid': return 'success';
        case 'Partial': return 'warning';
        case 'Unpaid': return 'danger';
        default: return 'info';
      }
    },

    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.pagination.currentPage = 1;
      this.fetchReconciliation();
    },

    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.fetchReconciliation();
    },

    handlePageSizeChange(pageSize) {
      this.pagination.perPage = pageSize;
      this.pagination.currentPage = 1;
      this.fetchReconciliation();
    },

    handleDateRangeTypeChange(value) {
      this.dateRangeType = value;
      if (value === 'custom') {
        this.filters.dateRange = null;
      } else if (this.selectedDate) {
        this.updateDateRangeFromSelectedDate();
      }
    },

    updateDateRangeFromSelectedDate() {
      if (!this.selectedDate) return;

      let dateObj = this.selectedDate;
      if (typeof dateObj === 'string') {
        const [year, month, day] = dateObj.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      const startDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
      const endDate = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0);

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      this.filters.dateRange = [formatDate(startDate), formatDate(endDate)];
    },

    goToPrevious() {
      if (!this.selectedDate) return;
      let dateObj = this.selectedDate;
      if (typeof dateObj === 'string') {
        const [year, month, day] = dateObj.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      }
      const newDate = new Date(dateObj);
      newDate.setMonth(newDate.getMonth() - 1);
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      this.selectedDate = `${year}-${month}-${day}`;
    },

    goToNext() {
      if (!this.selectedDate) return;
      let dateObj = this.selectedDate;
      if (typeof dateObj === 'string') {
        const [year, month, day] = dateObj.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      }
      const newDate = new Date(dateObj);
      newDate.setMonth(newDate.getMonth() + 1);
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      this.selectedDate = `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 1rem;
}

.summary-card {
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  text-align: center;
}

.summary-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
}

.summary-value.text-success {
  color: #67c23a;
}

.summary-value.text-danger {
  color: #f56c6c;
}

/* Filters */
.filters-container {
  --primary-color: #6ba6cd;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  display: flex;
  align-items: center;
  gap: 0.75rem;
  row-gap: 0.5rem;
  padding: 1rem;
  background: var(--background-glass);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-subtle);
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.filter-group-compact {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group-compact.date-group {
  min-width: 320px;
  flex-grow: 1;
  max-width: 480px;
}

/* Prev/next arrows live inside the date group, flanking the picker */
.filter-group-compact.date-nav-group {
  gap: 0.5rem;
}

.date-nav-group .compact-date-picker {
  flex: 1;
  min-width: 0;
}

.filter-group-compact.date-range-group {
  min-width: 350px;
  flex-grow: 1;
  max-width: 500px;
}

.compact-select {
  min-width: 150px;
}

.compact-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  width: 40px;
  min-width: 40px;
}

.compact-nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.compact-nav-btn i {
  font-size: 12px;
}

.compact-date-picker {
  width: 100%;
}

/* Upload Modal */
.upload-modal-body {
  padding: 1rem;
}

.upload-area {
  margin-bottom: 1rem;
}

.file-input {
  display: none;
}

.upload-placeholder {
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  color: rgba(255, 255, 255, 0.6);
}

.upload-placeholder:hover {
  border-color: rgba(255, 255, 255, 0.4);
}

.upload-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  display: block;
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #e0e0e0;
}

.remove-file-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
}

.remove-file-btn:hover {
  color: #f56c6c;
}

/* Upload Preview */
.upload-preview h6 {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
}

.preview-table-wrapper {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow-x: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.preview-table th,
.preview-table td {
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  color: #e0e0e0;
}

.preview-table th {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  position: sticky;
  top: 0;
}

/* Adjust Modal */
.adjust-modal-body {
  padding: 1rem;
}

.adjust-field {
  margin-bottom: 1rem;
}

.adjust-field label {
  display: block;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.25rem;
}

.adjust-value {
  color: #e0e0e0;
  font-size: 1rem;
}

.adjust-input {
  width: 100%;
  padding: 0.5rem;
  background-color: #656565;
  border: 1px solid #4a5568;
  border-radius: 4px;
  color: #ffffff;
  font-size: 1rem;
}

.adjust-input:focus {
  border-color: #4caf50;
  outline: none;
}

.adjust-textarea {
  width: 100%;
  padding: 0.5rem;
  background-color: #656565;
  border: 1px solid #4a5568;
  border-radius: 4px;
  color: #ffffff;
  font-size: 0.9rem;
  resize: vertical;
}

.adjust-textarea:focus {
  border-color: #4caf50;
  outline: none;
}

/* Create/Edit Payment Modal */
.payment-modal-body {
  padding: 1rem;
}

.payment-form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.payment-form-field {
  flex: 1;
  min-width: 0;
}

.payment-form-field label {
  display: block;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.25rem;
}

.full-width-picker {
  width: 100% !important;
}

/* Dialog Footer */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 1rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: transparent;
  border-color: rgba(255, 255, 255, 0.3);
  color: #e0e0e0;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.spinner-border-sm {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 0.15em solid rgba(255, 255, 255, 0.2);
  border-right-color: #e0e0e0;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Date picker styling */
::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

::v-deep .el-range-separator {
  color: #ffffff !important;
}

::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

@media (max-width: 991px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .page-header-row {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .header-actions {
    flex-wrap: wrap;
    row-gap: 0.5rem;
  }

  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group-compact,
  .filter-group-compact.date-group,
  .filter-group-compact.date-range-group {
    min-width: unset;
    max-width: unset;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }
}

/* Bulk Import Results */
.bulk-stat {
  padding: 10px;
  border-radius: 6px;
}

.bulk-stat-number {
  font-size: 24px;
  font-weight: 700;
}

.bulk-stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.bulk-stat-success {
  background: rgba(103, 194, 58, 0.1);
  color: #67c23a;
}

.bulk-stat-warning {
  background: rgba(230, 162, 60, 0.1);
  color: #e6a23c;
}

.bulk-stat-danger {
  background: rgba(245, 108, 108, 0.1);
  color: #f56c6c;
}

.bulk-detail-table-wrapper {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow-x: auto;
}

.bulk-detail-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.bulk-detail-table th {
  background: rgba(255, 255, 255, 0.05);
  padding: 6px 8px;
  text-align: left;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  position: sticky;
  top: 0;
}

.bulk-detail-table td {
  padding: 4px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  color: #e0e0e0;
}

.bulk-detail-table td:nth-child(2) {
  white-space: normal;
  min-width: 140px;
}

.bulk-detail-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}
</style>
